import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDialogFocus } from "../../../../hooks/useDialogFocus";
import { Dropdown } from "../../../../components/Dropdown";
import { ModalCloseButton } from "../../../../components/ModalCloseButton";
import { getCalls, updateCall } from "../../../../services/calls";
import type { CallResponse, OrganizationMember } from "../../../../types/api";
import { getSafeErrorMessage } from "../../../../utils/errorHandler";
import { formatPhoneNumber, toTelHref } from "../../../../utils/formatters";
import { formatCallDateTime, NO_TIME_LABEL } from "./callTime";
import "./CallModal.css";
import "./CallDetailModal.css";

interface CallDetailModalProps {
  /** The call to show. Null closes the modal. */
  call: CallResponse | null;
  /**
   * Assignable coaches for the reassign dropdown. Passed in rather than fetched here
   * because the list already loads them for its filter, and the modal opens per call.
   * An empty list (the fetch failed) falls back to showing the coach's name as text.
   */
  coaches?: OrganizationMember[];
  onClose: () => void;
  /** Called with the updated call after notes, the assigned coach, or the status change. */
  onUpdated: (call: CallResponse) => void;
  /** Optional "open player" affordance; omitted when already on the player's profile. */
  onOpenPlayer?: (prospectId: string) => void;
  /**
   * Hands the call up to be deleted. The owner runs the confirm and the request rather
   * than this modal, so there is one delete path and one piece of confirm copy no matter
   * whether the coach started from a row or from in here.
   */
  onDelete?: (call: CallResponse) => void;
}

/**
 * Detail view for a single call: who/when/status, click-to-dial, an editable
 * outcome-notes log, and that prospect's earlier calls so a coach can read what
 * happened on previous calls before dialing.
 *
 * Notes saved here land on `call.outcomeNotes`, and the API mirrors them onto the
 * prospect as an ordinary text recruiting note — so a call log reads back from the
 * player's Notes section with everything else written about them.
 *
 * Mount with `key={call.id}` so switching calls remounts (and reseeds) the editor.
 */
export function CallDetailModal({
  call,
  coaches = [],
  onClose,
  onUpdated,
  onOpenPlayer,
  onDelete,
}: CallDetailModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus({ isOpen: Boolean(call), dialogRef, handleEscape: false });

  const [notes, setNotes] = useState(call?.outcomeNotes ?? "");
  const [coachId, setCoachId] = useState(call?.assignedCoachId ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [history, setHistory] = useState<CallResponse[]>([]);
  // Starts true when there is a call to load history for, so the list does not flash
  // "no other calls" before the first fetch resolves.
  const [historyLoading, setHistoryLoading] = useState(call !== null);

  // Guards against an out-of-order history response.
  const historyTokenRef = useRef(0);

  const callId = call?.id ?? null;
  const prospectId = call?.prospectId ?? null;
  const isComplete = call?.status === "complete";
  const notesChanged = notes !== (call?.outcomeNotes ?? "");
  const coachChanged = !!call && coachId !== call.assignedCoachId;
  const hasChanges = notesChanged || coachChanged;

  /**
   * The org's coaches, plus the currently assigned one when they are not among them.
   * A coach who has since been deactivated still holds their calls, and dropping them
   * from the options would render the picker empty — reading as unassigned rather than
   * as "assigned to someone no longer on the staff list".
   */
  const coachOptions = useMemo(() => {

    // No roster of coaches at all means the fetch failed, which is a different case
    // from "the assigned coach is missing from it" — there is nothing to pick, so the
    // caller falls back to plain text rather than a one-entry picker that cannot reassign.
    if (coaches.length === 0) return [];

    const options = coaches.map((c) => ({
      label: c.displayName,
      value: c.id,
    }));
    if (call && !options.some((o) => o.value === call.assignedCoachId)) {
      options.unshift({
        label: call.assignedCoachName,
        value: call.assignedCoachId,
      });
    }
    return options;
  }, [coaches, call]);

  useEffect(() => {
    if (!prospectId) {
      setHistory([]);
      setHistoryLoading(false);
      return;
    }

    const token = ++historyTokenRef.current;
    setHistoryLoading(true);
    getCalls({ prospectId, pageSize: 100 })
      .then((response) => {
        if (token !== historyTokenRef.current) return;
        const sorted = [...response.items].sort((a, b) => {
          // Unscheduled calls first, matching the server's ordering. A call with no time is the
          // one still needing action, and without an explicit branch it would land wherever
          // `new Date(null)` coerces to, which is 1970 — the far past.
          if (!a.scheduledAt && !b.scheduledAt) return 0;
          if (!a.scheduledAt) return -1;
          if (!b.scheduledAt) return 1;
          return (
            new Date(b.scheduledAt).getTime() - new Date(a.scheduledAt).getTime()
          );
        });
        setHistory(sorted);
      })
      .catch((err) => {
        if (token !== historyTokenRef.current) return;
        // Non-fatal: the detail pane still works without the history.
        console.error("Failed to load call history:", err);
        setHistory([]);
      })
      .finally(() => {
        if (token === historyTokenRef.current) setHistoryLoading(false);
      });
  }, [prospectId, callId]);

  // Esc and overlay clicks close the modal, but not over unsaved edits — losing a
  // typed-out call log, or a reassignment, to a stray click is worse than an extra
  // deliberate click.
  const canDismiss = !saving && !hasChanges;

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && call && canDismiss) {
        onClose();
      }
    };
    if (call) {
      document.addEventListener("keydown", handleEsc);
      return () => document.removeEventListener("keydown", handleEsc);
    }
  }, [call, canDismiss, onClose]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget && canDismiss) {
        onClose();
      }
    },
    [onClose, canDismiss]
  );

  const save = async (alsoComplete: boolean) => {
    if (!call) return;
    setSaving(true);
    setError(null);
    try {
      // Notes only ride along when they actually changed. Sending them on a
      // reassign-only save would re-mirror the note onto the player's profile and
      // rewrite its timestamp for an edit the coach never made — the same reason the
      // API only syncs the note when the field is present on the request.
      //
      // When they did change, send "" rather than null to clear one: the PATCH contract
      // cannot tell an explicit null from an omitted field, so null would leave the old
      // note in place.
      const updated = await updateCall(call.id, {
        ...(notesChanged ? { outcomeNotes: notes.trim() } : {}),
        ...(coachChanged ? { assignedCoachId: coachId } : {}),
        ...(alsoComplete ? { status: "complete" as const } : {}),
      });
      onUpdated(updated);
      onClose();
    } catch (err) {
      setError(getSafeErrorMessage(err, "Failed to save this call"));
      console.error("Failed to save call:", err);
    } finally {
      setSaving(false);
    }
  };

  if (!call) return null;

  const dialHref = toTelHref(call.prospectPhone);
  // Strictly earlier calls only, so "earlier calls" never lists next week's call.
  const earlierCalls = history.filter(
    (c) =>
      c.id !== call.id &&
      // "Earlier" needs two times to compare. An unscheduled call is not earlier than
      // anything, and nothing is earlier than an unscheduled one.
      !!c.scheduledAt &&
      !!call.scheduledAt &&
      new Date(c.scheduledAt).getTime() < new Date(call.scheduledAt).getTime()
  );

  return (
    <div className="schedule-call-modal__overlay" onClick={handleOverlayClick}>
      <div ref={dialogRef} tabIndex={-1}
        className="schedule-call-modal__content call-detail-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="call-detail-modal-title"
        data-testid="call-detail-modal"
      >
        <div className="schedule-call-modal__header">
          <div className="schedule-call-modal__header-content">
            <h3
              id="call-detail-modal-title"
              className="schedule-call-modal__title"
            >
              {call.prospectName}
            </h3>
            <p className="schedule-call-modal__subtitle">
              {call.scheduledAt ? formatCallDateTime(call.scheduledAt) : NO_TIME_LABEL}
            </p>
          </div>
          <ModalCloseButton onClick={onClose} disabled={saving} />
        </div>

        <div className="schedule-call-modal__body">
          <div className="call-detail-modal__facts">
            <span
              className={`call-detail-modal__pill call-detail-modal__pill--${call.status}`}
            >
              {isComplete ? "Complete" : "Upcoming"}
            </span>
            {coachOptions.length > 0 ? (
              /* Editable in place: reassigning a call is a routine correction, and the
                 API's PATCH already re-arms the reminder and notifies the new coach. */
              <div
                className="call-detail-modal__coach"
                data-testid="call-detail-coach-field"
              >
                <Dropdown
                  label="Coach"
                  options={coachOptions}
                  value={coachId}
                  onSelect={setCoachId}
                  disabled={saving}
                  searchable={coachOptions.length > 8}
                  searchPlaceholder="Search coaches..."
                />
              </div>
            ) : (
              /* The coach list failed to load, so there is nothing to pick from.
                 Say who holds the call rather than showing an empty picker. */
              <span className="call-detail-modal__fact">
                Coach: {call.assignedCoachName}
              </span>
            )}
            {call.completedAt && (
              <span className="call-detail-modal__fact">
                Logged {formatCallDateTime(call.completedAt)}
              </span>
            )}
          </div>

          <div className="call-detail-modal__actions">
            {dialHref ? (
              <a
                href={dialHref}
                className="call-detail-modal__call-link"
                data-testid="call-detail-dial"
              >
                Call {formatPhoneNumber(call.prospectPhone ?? "")}
              </a>
            ) : (
              <span className="call-detail-modal__fact">
                No phone number on file
              </span>
            )}
            {onOpenPlayer && (
              <button
                type="button"
                className="call-detail-modal__secondary call-detail-modal__secondary--link"
                data-testid="call-detail-open-player"
                onClick={() => onOpenPlayer(call.prospectId)}
              >
                Open player
              </button>
            )}
          </div>

          <div className="schedule-call-modal__field">
            <label
              className="schedule-call-modal__label"
              htmlFor="call-detail-notes"
            >
              How did the call go?
            </label>
            <textarea
              id="call-detail-notes"
              className="schedule-call-modal__input schedule-call-modal__textarea call-detail-modal__notes-input"
              data-testid="call-detail-notes"
              rows={5}
              maxLength={5000}
              placeholder="Log what you talked about, next steps, who else was on the call..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={saving}
            />
            <p className="call-detail-modal__hint">
              Saved as a note on {call.prospectName}&apos;s profile, alongside
              every other note on the player.
            </p>
          </div>

          <div className="call-detail-modal__history">
            <h4 className="call-detail-modal__history-title">
              Earlier calls with {call.prospectName}
            </h4>
            {historyLoading && (
              <p className="call-detail-modal__fact">Loading history...</p>
            )}
            {!historyLoading && earlierCalls.length === 0 && (
              <p className="call-detail-modal__fact">
                No earlier calls logged.
              </p>
            )}
            {!historyLoading && earlierCalls.length > 0 && (
              <ul className="call-detail-modal__history-list">
                {earlierCalls.map((earlier) => (
                  <li
                    key={earlier.id}
                    className="call-detail-modal__history-item"
                    data-testid={`call-detail-history-${earlier.id}`}
                  >
                    <div className="call-detail-modal__history-head">
                      <span className="call-detail-modal__history-date">
                        {earlier.scheduledAt
                          ? formatCallDateTime(earlier.scheduledAt)
                          : NO_TIME_LABEL}
                      </span>
                      <span
                        className={`call-detail-modal__pill call-detail-modal__pill--${earlier.status}`}
                      >
                        {earlier.status === "complete"
                          ? "Complete"
                          : "Upcoming"}
                      </span>
                    </div>
                    <p className="call-detail-modal__history-notes">
                      {earlier.outcomeNotes || "No notes logged."}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {error && (
            <div className="schedule-call-modal__error" role="alert">
              {error}
            </div>
          )}
        </div>

        <div className="schedule-call-modal__footer call-detail-modal__footer">
          {onDelete && (
            /* Pushed to the far left, away from Save, so the destructive action is never
               the one hit by muscle memory aiming for the primary button. */
            <button
              type="button"
              className="call-detail-modal__delete"
              data-testid="call-detail-delete"
              onClick={() => onDelete(call)}
              disabled={saving}
            >
              Delete call
            </button>
          )}
          <button
            type="button"
            className="schedule-call-modal__cancel-button"
            onClick={onClose}
            disabled={saving}
          >
            Close
          </button>
          {!isComplete && (
            <button
              type="button"
              className="schedule-call-modal__cancel-button"
              data-testid="call-detail-save-complete"
              onClick={() => save(true)}
              disabled={saving}
            >
              Save &amp; complete
            </button>
          )}
          <button
            type="button"
            className="schedule-call-modal__save-button"
            data-testid="call-detail-save"
            onClick={() => save(false)}
            disabled={saving || !hasChanges}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
